import React, { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Cloud, Server, Shield, Lock, Network, Database, Globe, Key,
  CheckCircle2, XCircle, Loader2, AlertTriangle, ArrowRight,
  Download, Building2, Zap, HelpCircle, Mail, Trash2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * DataPlaneSelector - P6.1: Clear "Niyant Hosted" vs "Deploy Own" selection
 */
export function DataPlaneSelector({ onSelect, eligibility, loading }) {
  return (
    <div className="space-y-6" data-testid="data-plane-selector">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Choose Data Plane</h2>
        <p className="text-sm text-[var(--n-text-muted)] mt-1">
          Where should Niyant store your Azure resource data?
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Niyant Hosted */}
        <Card 
          className="relative cursor-pointer border-2 hover:border-[var(--hig-accent)] transition-colors bg-[var(--n-surface)] border-[var(--n-border)]"
          onClick={() => onSelect("niyant_hosted")}
          data-testid="option-niyant-hosted"
        >
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Cloud className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <CardTitle className="text-base">Niyant Hosted</CardTitle>
                <Badge variant="outline" className="text-xs mt-1">Recommended</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <CardDescription className="text-sm">
              We manage the infrastructure. Get started in minutes with zero Azure setup.
            </CardDescription>
            <ul className="space-y-1.5 text-xs text-[var(--n-text-muted)]">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                No Azure resources to manage
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Automatic backups & updates
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Multi-tenant isolation
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Available on all plans
              </li>
            </ul>
            <div className="pt-2 border-t border-[var(--n-border)]">
              <span className="text-xs font-medium text-blue-500">Best for: Most users</span>
            </div>
          </CardContent>
        </Card>

        {/* Customer Hosted */}
        <Card 
          className={`relative border-2 transition-colors bg-[var(--n-surface)] ${
            eligibility?.eligible 
              ? "cursor-pointer hover:border-[var(--hig-accent)] border-[var(--n-border)]"
              : "opacity-60 border-[var(--n-border)]"
          }`}
          onClick={() => eligibility?.eligible && onSelect("customer_hosted")}
          data-testid="option-customer-hosted"
        >
          {!eligibility?.eligible && (
            <div className="absolute inset-0 bg-[var(--n-bg)]/50 backdrop-blur-[1px] rounded-lg flex items-center justify-center z-10">
              <Badge variant="secondary" className="text-xs">
                <Lock className="w-3 h-3 mr-1" />
                Enterprise Plan Required
              </Badge>
            </div>
          )}
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <CardTitle className="text-base">Deploy Own Data Plane</CardTitle>
                <Badge variant="outline" className="text-xs mt-1 border-emerald-500 text-emerald-500">Enterprise</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <CardDescription className="text-sm">
              Deploy to your Azure subscription. Full data sovereignty and custom networking.
            </CardDescription>
            <ul className="space-y-1.5 text-xs text-[var(--n-text-muted)]">
              <li className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-emerald-500" />
                Data stays in your Azure tenant
              </li>
              <li className="flex items-center gap-2">
                <Network className="w-3.5 h-3.5 text-emerald-500" />
                Private Link connectivity
              </li>
              <li className="flex items-center gap-2">
                <Key className="w-3.5 h-3.5 text-emerald-500" />
                Your Key Vault for secrets
              </li>
              <li className="flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-emerald-500" />
                Your Cosmos DB instance
              </li>
            </ul>
            <div className="pt-2 border-t border-[var(--n-border)]">
              <span className="text-xs font-medium text-emerald-500">Best for: Regulated industries</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {!eligibility?.eligible && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Enterprise Feature</p>
              <p className="text-xs text-[var(--n-text-muted)] mt-1">
                {eligibility?.reason || "Upgrade to Enterprise plan to deploy your own data plane."}
              </p>
              {eligibility?.upgrade_url && (
                <Button variant="link" className="h-auto p-0 text-xs text-amber-500 mt-1" onClick={() => window.location.href = eligibility.upgrade_url}>
                  View upgrade options <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * RegionSelector - P6.2: Region dropdown with latency warnings
 */
export function RegionSelector({ value, onChange, regions, controlPlaneRegion, onLatencyCheck }) {
  const [latencyWarning, setLatencyWarning] = useState(null);

  useEffect(() => {
    if (value && value !== controlPlaneRegion) {
      onLatencyCheck?.(value).then(result => {
        setLatencyWarning(result?.warning);
      });
    } else {
      setLatencyWarning(null);
    }
  }, [value, controlPlaneRegion, onLatencyCheck]);

  return (
    <div className="space-y-2" data-testid="region-selector">
      <Label className="text-xs font-medium">Deployment Region *</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-[var(--n-bg)] border-[var(--n-border-2)]" data-testid="select-region">
          <SelectValue placeholder="Select a region" />
        </SelectTrigger>
        <SelectContent>
          {regions.map(r => (
            <SelectItem key={r.value} value={r.value}>
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5" />
                {r.label}
                {r.recommended && (
                  <Badge variant="outline" className="text-[10px] ml-1 py-0 px-1 border-emerald-500 text-emerald-500">
                    <Zap className="w-2.5 h-2.5 mr-0.5" /> Optimal
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {latencyWarning && (
        <div className="flex items-start gap-2 p-2 rounded bg-amber-500/10 text-xs text-amber-600">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{latencyWarning}</span>
        </div>
      )}
    </div>
  );
}

/**
 * DeploymentProgress - P6.3: Real-time deployment progress
 */
export function DeploymentProgress({ progress, status }) {
  const steps = [
    { key: "validate_permissions", label: "Validating Permissions", icon: Shield },
    { key: "create_resource_group", label: "Creating Resource Group", icon: Server },
    { key: "deploy_network", label: "Deploying Network", icon: Network },
    { key: "deploy_key_vault", label: "Deploying Key Vault", icon: Key },
    { key: "deploy_cosmos_db", label: "Deploying Cosmos DB", icon: Database },
    { key: "deploy_log_analytics", label: "Deploying Log Analytics", icon: Cloud },
    { key: "create_private_endpoint", label: "Creating Private Endpoint", icon: Lock },
    { key: "configure_connection", label: "Configuring Connection", icon: Globe },
    { key: "verify_connectivity", label: "Verifying Connectivity", icon: CheckCircle2 },
  ];

  const getStepStatus = (stepKey) => {
    const step = progress?.find(p => p.step === stepKey);
    return step?.status || "pending";
  };

  return (
    <div className="space-y-4" data-testid="deployment-progress">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Deployment Progress</h3>
        <Badge variant={status === "success" ? "default" : status === "failed" ? "destructive" : "secondary"}>
          {status || "In Progress"}
        </Badge>
      </div>

      <div className="space-y-2">
        {steps.map((step, i) => {
          const stepStatus = getStepStatus(step.key);
          const Icon = step.icon;
          
          return (
            <div key={step.key} className="flex items-center gap-3 p-2 rounded bg-[var(--n-surface)]">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                stepStatus === "completed" ? "bg-emerald-500/10 text-emerald-500" :
                stepStatus === "in_progress" ? "bg-blue-500/10 text-blue-500" :
                stepStatus === "failed" ? "bg-red-500/10 text-red-500" :
                stepStatus === "skipped" ? "bg-gray-500/10 text-gray-500" :
                "bg-[var(--n-border)] text-[var(--n-text-muted)]"
              }`}>
                {stepStatus === "completed" ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                 stepStatus === "in_progress" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                 stepStatus === "failed" ? <XCircle className="w-3.5 h-3.5" /> :
                 <Icon className="w-3.5 h-3.5" />}
              </div>
              <span className={`text-xs ${stepStatus === "in_progress" ? "font-medium" : ""}`}>
                {step.label}
              </span>
              {stepStatus === "skipped" && (
                <Badge variant="outline" className="text-[10px] ml-auto">Skipped</Badge>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * UpgradePrompt - P6.4: CTA for non-enterprise users
 */
export function UpgradePrompt({ feature, currentPlan }) {
  return (
    <Card className="bg-gradient-to-br from-[var(--hig-accent)]/5 to-[var(--hig-accent)]/10 border-[var(--hig-accent)]/20" data-testid="upgrade-prompt">
      <CardContent className="p-4 flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-[var(--hig-accent)]/10 flex items-center justify-center flex-shrink-0">
          <Lock className="w-5 h-5 text-[var(--hig-accent)]" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium">Unlock {feature}</h4>
          <p className="text-xs text-[var(--n-text-muted)] mt-1">
            This feature is available on Enterprise plans. Upgrade to deploy your own data plane with full data sovereignty.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" className="h-7 text-xs bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent-hover)]">
              View Plans <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs">
              <Mail className="w-3 h-3 mr-1" /> Contact Sales
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * CleanupWarningModal - P5.1 + P5.2: Destructive action warning with name confirmation
 */
export function CleanupWarningModal({ isOpen, onClose, onConfirm, warningData, loading }) {
  const [confirmationName, setConfirmationName] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [purgeMetadata, setPurgeMetadata] = useState(true);

  const isValid = confirmationName.toLowerCase() === warningData?.confirmation_required?.expected_value?.toLowerCase();

  const handleConfirm = () => {
    if (isValid) {
      onConfirm({
        confirmation_name: confirmationName,
        send_revocation_email: sendEmail,
        purge_metadata: purgeMetadata,
      });
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-lg" data-testid="cleanup-warning-modal">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-500">
            <AlertTriangle className="w-5 h-5" />
            {warningData?.title || "Delete Data Plane"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {warningData?.message}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* Resources to delete */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-[var(--n-text-muted)]">Resources that will be deleted:</h4>
            <div className="space-y-1.5">
              {warningData?.resources_to_delete?.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs p-2 rounded bg-red-500/5">
                  <Database className="w-3.5 h-3.5 text-red-500" />
                  <span className="font-medium">{r.type}</span>
                  <span className="text-[var(--n-text-muted)]">{r.name}</span>
                  {r.warning && <Badge variant="destructive" className="text-[10px] ml-auto">{r.warning}</Badge>}
                </div>
              ))}
            </div>
          </div>

          {/* Warnings */}
          <div className="space-y-1.5 p-3 rounded bg-amber-500/10 border border-amber-500/20">
            {warningData?.warnings?.map((w, i) => (
              <p key={i} className="text-xs text-amber-600 flex items-start gap-2">
                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                {w}
              </p>
            ))}
          </div>

          {/* Options */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} className="rounded" />
              Send app revocation instructions email
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={purgeMetadata} onChange={e => setPurgeMetadata(e.target.checked)} className="rounded" />
              Remove all metadata from Niyant
            </label>
          </div>

          {/* Confirmation input */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">
              {warningData?.confirmation_required?.prompt}
            </Label>
            <Input
              value={confirmationName}
              onChange={e => setConfirmationName(e.target.value)}
              placeholder={warningData?.confirmation_required?.expected_value}
              className="font-mono text-sm"
              data-testid="cleanup-confirmation-input"
            />
            {confirmationName && !isValid && (
              <p className="text-xs text-red-500">Name doesn't match</p>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || loading}
            variant="destructive"
            data-testid="cleanup-confirm-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete Data Plane
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * DriftAlertCard - Display drift alerts with actions
 */
export function DriftAlertCard({ alert, onAcknowledge, onResolve }) {
  const severityColors = {
    critical: "bg-red-500/10 border-red-500/30 text-red-500",
    high: "bg-orange-500/10 border-orange-500/30 text-orange-500",
    medium: "bg-amber-500/10 border-amber-500/30 text-amber-500",
    low: "bg-blue-500/10 border-blue-500/30 text-blue-500",
  };

  const severityIcons = {
    critical: XCircle,
    high: AlertTriangle,
    medium: AlertTriangle,
    low: HelpCircle,
  };

  const Icon = severityIcons[alert.severity] || AlertTriangle;

  return (
    <Card className={`border ${severityColors[alert.severity] || severityColors.medium}`} data-testid="drift-alert-card">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{alert.drift_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</h4>
              <Badge variant={alert.severity === "critical" ? "destructive" : "secondary"} className="text-[10px]">
                {alert.severity}
              </Badge>
            </div>
            <p className="text-xs text-[var(--n-text-muted)]">{alert.message}</p>
            {alert.resource_name && (
              <p className="text-xs font-mono bg-[var(--n-surface)] p-1.5 rounded">
                {alert.resource_name}
              </p>
            )}
            <p className="text-xs text-[var(--n-text-muted)]">
              <strong>Recommendation:</strong> {alert.recommendation}
            </p>
            <div className="flex items-center gap-2 pt-2">
              {!alert.acknowledged && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAcknowledge?.(alert.id)}>
                  Acknowledge
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onResolve?.(alert.id)}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Resolved
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default {
  DataPlaneSelector,
  RegionSelector,
  DeploymentProgress,
  UpgradePrompt,
  CleanupWarningModal,
  DriftAlertCard,
};
